
const normalizeQuestions = (questions) => {
  if (!questions) return {};
  if (Array.isArray(questions)) {
    return questions.reduce((acc, question) => {
      if (!question?.id) return acc;
      acc[question.id] =
        question.answer !== undefined
          ? question.answer
          : question.value !== undefined
          ? question.value
          : '';
      return acc;
    }, {});
  }
  return questions || {};
};

const hasNoCustomData = (customData) => {
  if (!customData) return true;
  if (Array.isArray(customData)) {
    return customData.length === 0;
  }
  return Object.keys(customData).length === 0;
};

const getGoalCustomSectionIds = (allGoalData) => {
  return (
    allGoalData?.DataEntrySelection?.CustomDataEntrySections ||
    allGoalData?.dataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    allGoalData?.dataCollection?.DataCollection?.DataEntrySelection
      ?.CustomDataEntrySections ||
    allGoalData?.DataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    []
  );
};

export const convertCustomDataToCustomSection = (
  customData,
  allGoalData
) => {
  if (hasNoCustomData(customData)) {
    return [];
  }

  const customSectionIds = getGoalCustomSectionIds(allGoalData);
  const shouldFilterByIds =
    Array.isArray(customSectionIds) && customSectionIds.length > 0;

  const hasSectionResponses = (questions) => {
    if (!questions) return false;
    if (Array.isArray(questions)) {
      return questions.some((question) => {
        const answer =
          question?.answer !== undefined
            ? question.answer
            : question?.value !== undefined
            ? question.value
            : '';
        if (Array.isArray(answer)) return answer.length > 0;
        if (answer && typeof answer === 'object') return true;
        return answer !== null && answer !== undefined && answer !== '';
      });
    }
    if (typeof questions === 'object') {
      return Object.values(questions).some((value) => {
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') {
          if ('answer' in value && value.answer !== undefined) {
            const answer = value.answer;
            if (Array.isArray(answer)) return answer.length > 0;
            return answer !== null && answer !== '';
          }
          if ('value' in value && value.value !== undefined) {
            const v = value.value;
            if (Array.isArray(v)) return v.length > 0;
            return v !== null && v !== '';
          }
          return true;
        }
        return value !== null && value !== undefined && value !== '';
      });
    }
    return false;
  };

  const shouldKeepSection = (sectionId, questions) => {
    if (!sectionId) return false;
    if (!shouldFilterByIds) return true;
    if (customSectionIds.includes(sectionId)) return true;
    return hasSectionResponses(questions);
  };

  if (Array.isArray(customData)) {
    return customData
      .filter((section) => shouldKeepSection(section?.docId, section?.questions))
      .map((section) => ({
        docId: section.docId,
        title: section.title,
        definition: section.definition,
        questions: normalizeQuestions(section.questions),
      }));
  }

  // Check if this is the old flat format (keys contain underscores)
  const hasOldFormat = Object.keys(customData).some((key) => key.includes('_'));
  if (hasOldFormat) {
    const sectionMap = new Map();
    Object.entries(customData).forEach(([key, value]) => {
      const [sectionId, questionId] = key.split('_');
      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          docId: sectionId,
          questions: {},
        });
      }
      sectionMap.get(sectionId).questions[questionId] = value;
    });

    return Array.from(sectionMap.values()).filter((section) =>
      shouldKeepSection(section.docId, section.questions)
    );
  }

  const customSections = [];

  Object.entries(customData).forEach(([sectionId, questions]) => {
    if (shouldKeepSection(sectionId, questions)) {
      customSections.push({
        docId: sectionId,
        questions: normalizeQuestions(questions),
      });
    }
  });
  return customSections;
};

export const getCriteriaFromGoalData = (allGoalData) => {
  if (!allGoalData) {
    return [];
  }

  const subgoals =
    allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
    allGoalData?.HasSubgoal?.Subgoals ||
    [];

  const currentSubgoal = subgoals?.find(
    (subgoal) => subgoal?.CurrentGoal === 'Yes'
  );

  if (
    currentSubgoal &&
    currentSubgoal?.Criteria &&
    currentSubgoal?.Criteria?.length > 0
  ) {
    return currentSubgoal.Criteria;
  } else {
    const mainCriteria =
      allGoalData?.dataCollection?.DataCollection?.measurementType?.Criteria ||
      allGoalData?.DataCollection?.measurementType?.Criteria ||
      [];

    return mainCriteria;
  }
};

export const getPromptingTypeFromGoalData = (
  allGoalData,
  subGoalIndex,
  prompting
) => {
  if (!allGoalData) {
    return '';
  }

  let basePromptingType = null;
  if (!subGoalIndex && prompting !== undefined && prompting !== null) {
    basePromptingType = prompting;
  }

  const subgoals =
    allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
    allGoalData?.HasSubgoal?.Subgoals ||
    [];

  let currentSubgoal = null;

  if (subGoalIndex) {
    currentSubgoal = subgoals?.find(
      (sg) =>
        sg?.SubgoalNumber === subGoalIndex ||
        sg?.SubgoalNumber === parseInt(subGoalIndex)
    );

    // If SubgoalNumber property doesn't exist, fall back to positional index (1-based)
    if (!currentSubgoal && Array.isArray(subgoals)) {
      const idx = parseInt(subGoalIndex) - 1;
      if (idx >= 0 && idx < subgoals.length) {
        currentSubgoal = subgoals[idx];
      }
    }
  } else {
    currentSubgoal = null;
  }

  if (subGoalIndex) {
    if (
      currentSubgoal &&
      currentSubgoal?.PromptingApplicable?.isPromptingApplicable === 'Yes' &&
      currentSubgoal?.PromptingApplicable?.PromptingType
    ) {
      return currentSubgoal.PromptingApplicable.PromptingType;
    } else {
      return '';
    }
  } else {
    const mainPromptingType =
      basePromptingType ??
      allGoalData?.DataCollection?.PromptingApplicable?.PromptingType ??
      allGoalData?.dataCollection?.DataCollection?.PromptingApplicable
        ?.PromptingType ??
      '';
    return mainPromptingType;
  }
};

export const getPromptingLevelFromGoalData = (allGoalData) => {
  if (!allGoalData) {
    return '';
  }

  const subgoals =
    allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
    allGoalData?.HasSubgoal?.Subgoals ||
    [];

  const currentSubgoal = subgoals?.find(
    (subgoal) => subgoal?.CurrentGoal === 'Yes'
  );

  let level = '';
  if (currentSubgoal && currentSubgoal?.PromptingLevel) {
    level = currentSubgoal.PromptingLevel;
  } else {
    level =
      allGoalData?.dataCollection?.DataCollection?.measurementType
        ?.PromptingLevel ||
      allGoalData?.DataCollection?.measurementType?.PromptingLevel ||
      '';
  }

  return level;
};

export const getSubGoalName = (displayedSubGoalIndex, allGoalData) => {
  if (!displayedSubGoalIndex || !allGoalData) return null;

  const subgoals =
    allGoalData?.HasSubgoal?.Subgoals ||
    allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
    [];

  const targetSubgoal = subgoals.find(
    (subgoal) =>
      subgoal.SubgoalNumber === displayedSubGoalIndex ||
      subgoal.SubgoalNumber === parseInt(displayedSubGoalIndex)
  );

  return targetSubgoal?.SubgoalTitle || null;
};
